import React, { useEffect, useState, useCallback, useRef, forwardRef } from "react";
import { Loader2, CheckCircle2, Briefcase, Upload, Trash2, AlertCircle } from "lucide-react";
import { publicClient } from "@/lib/apiClient";
import {
  CAREER_CATEGORIES,
  CATEGORY_LABEL,
} from "@/data/careerTemplates";
import { isImageFile, formatBytes } from "@/lib/imageUtils";

const FILE_MAX_BYTES = 3 * 1024 * 1024; // 3MB
const MAX_FILES = 5;

const INITIAL = {
  listing_id: "",
  position_title: "",
  category: "",
  full_name: "",
  phone: "",
  email: "",
  city: "",
  current_status: "",
  relevant_experience: "",
  has_construction_experience: null,
  has_sales_experience: null,
  has_reliable_transportation: null,
  has_own_tools: null,
  comfortable_with_field_work: null,
  comfortable_with_commission: null,
  desired_compensation: "",
  availability: "",
  why_black_ridge: "",
  additional_notes: "",
  files: [],
  website: "",
};

const YES_NO_OPTIONS = [
  { label: "Yes", value: true },
  { label: "No", value: false },
  { label: "N/A", value: null },
];

function YesNoToggle({ value, onChange, name }) {
  return (
    <div className="flex gap-1 flex-wrap" data-testid={`yesno-${name}`}>
      {YES_NO_OPTIONS.map((opt) => {
        const isActive = value === opt.value;
        return (
          <button
            key={opt.label}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`px-3 py-1 rounded text-[11px] uppercase tracking-widest font-semibold border transition ${
              isActive
                ? "bg-br-bronze border-br-bronze text-white"
                : "bg-white border-gray-200 text-gray-600 hover:border-br-bronze"
            }`}
            data-testid={`yesno-${name}-${opt.label.toLowerCase()}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Reads a File into a base64 data URL. Returns { file metadata, data_url }.
 */
function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * CareerApplicationForm
 *
 * Used both on the main /careers page (generic application) and inline on a
 * job listing card (with the position pre-filled).
 *
 * Submits to POST /api/careers/apply with files embedded as base64 so
 * they land in the admin dashboard alongside the rest of the application.
 */
const CareerApplicationForm = forwardRef(function CareerApplicationForm({ prefill, listingMeta }, ref) {
  const [form, setForm] = useState(INITIAL);
  const [state, setState] = useState({ loading: false, success: false, error: "" });
  const inputRef = useRef(null);

  // Apply prefill from parent (listing card “Apply” button).
  useEffect(() => {
    if (!prefill) return;
    setForm((prev) => ({
      ...prev,
      ...(prefill.listing_id ? { listing_id: prefill.listing_id } : {}),
      ...(prefill.position_title ? { position_title: prefill.position_title } : {}),
      ...(prefill.category ? { category: prefill.category } : {}),
    }));
    setState((s) => (s.success ? { loading: false, success: false, error: "" } : s));
  }, [prefill]);

  const update = useCallback((k) => (e) => {
    const v = e?.target?.value ?? e;
    setForm((p) => ({ ...p, [k]: v }));
  }, []);

  const handleFiles = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const remaining = MAX_FILES - form.files.length;
    const accepted = files.slice(0, remaining);
    const next = [...form.files];
    const errors = [];
    for (const f of accepted) {
      if (f.size > FILE_MAX_BYTES) {
        errors.push(`${f.name}: exceeds 3MB. Compress and try again.`);
        continue;
      }
      try {
        const dataUrl = await readFileAsDataUrl(f);
        next.push({
          filename: f.name,
          content_type: f.type || "application/octet-stream",
          size: f.size,
          data_url: dataUrl,
          label: isImageFile(f) ? "portfolio" : "resume",
        });
      } catch (err) {
        errors.push(`${f.name}: failed to read file.`);
      }
    }
    if (errors.length) {
      setState({ loading: false, success: false, error: errors.join(" ") });
    } else {
      setState((s) => (s.error ? { ...s, error: "" } : s));
    }
    setForm((p) => ({ ...p, files: next }));
    if (inputRef.current) inputRef.current.value = "";
  }, [form.files]);

  const removeFile = (idx) => {
    setForm((p) => ({ ...p, files: p.files.filter((_, i) => i !== idx) }));
  };

  const submit = useCallback(async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.email) {
      setState({ loading: false, success: false, error: "Name, phone, and email are required." });
      return;
    }
    setState({ loading: true, success: false, error: "" });
    try {
      await publicClient.post("/careers/apply", form);
      setState({ loading: false, success: true, error: "" });
      // Reset form for the next applicant but preserve the listing context
      // so a re-apply (rare) still uses the same prefill.
      setForm((p) => ({
        ...INITIAL,
        listing_id: p.listing_id,
        position_title: p.position_title,
        category: p.category,
      }));
    } catch (err) {
      setState({
        loading: false,
        success: false,
        error: err?.response?.data?.detail || "Something went wrong, please try again.",
      });
    }
  }, [form]);

  if (state.success) {
    return (
      <div
        ref={ref}
        id="careers-apply"
        className="bg-white border border-gray-200 rounded-lg overflow-hidden scroll-mt-24"
        data-testid="career-application-form"
      >
        <div className="p-6">
          <div className="flex items-start gap-3 bg-br-paper border border-br-bronze rounded p-5" data-testid="career-application-success">
            <CheckCircle2 size={22} className="text-br-bronze shrink-0 mt-0.5" />
            <div>
              <div className="font-display tracking-widest uppercase text-lg text-br-black">Application Received</div>
              <p className="text-sm text-gray-700 mt-1 leading-relaxed">
                Thank you for applying with Black Ridge Construction. We received your information and will review it before reaching out.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      id="careers-apply"
      className="bg-white border border-gray-200 rounded-lg overflow-hidden scroll-mt-24"
      data-testid="career-application-form"
    >
      <div className="px-6 py-5 border-b border-gray-200 bg-br-paper flex items-start gap-3">
        <Briefcase size={20} className="text-br-bronze shrink-0 mt-1" />
        <div>
          <div className="br-eyebrow text-br-bronze">Apply</div>
          <h3 className="font-display text-2xl tracking-widest uppercase mt-1">
            {listingMeta?.title ? `Apply for ${listingMeta.title}` : "Apply to Black Ridge"}
          </h3>
          <p className="text-sm text-gray-700 mt-2 max-w-2xl">
            Tell us a little about yourself and your experience. We’ll review your application and follow up if it looks like a fit.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="p-6 grid sm:grid-cols-2 gap-4" data-testid="career-form-fields">
        <div>
          <label className="label-br" htmlFor="caf-name">Full Name *</label>
          <input id="caf-name" className="input-br" value={form.full_name} onChange={update("full_name")} required data-testid="caf-name" />
        </div>
        <div>
          <label className="label-br" htmlFor="caf-phone">Phone *</label>
          <input id="caf-phone" type="tel" className="input-br" value={form.phone} onChange={update("phone")} required data-testid="caf-phone" />
        </div>
        <div>
          <label className="label-br" htmlFor="caf-email">Email *</label>
          <input id="caf-email" type="email" className="input-br" value={form.email} onChange={update("email")} required data-testid="caf-email" />
        </div>
        <div>
          <label className="label-br" htmlFor="caf-city">City / Location</label>
          <input id="caf-city" className="input-br" value={form.city} onChange={update("city")} placeholder="Austin, Round Rock…" data-testid="caf-city" />
        </div>
        <div>
          <label className="label-br" htmlFor="caf-position">Position Applying For</label>
          <input id="caf-position" className="input-br" value={form.position_title} onChange={update("position_title")} placeholder="e.g. Sales Representative" data-testid="caf-position" />
        </div>
        <div>
          <label className="label-br" htmlFor="caf-category">Position Category</label>
          <select id="caf-category" className="select-br" value={form.category} onChange={update("category")} data-testid="caf-category">
            <option value="">Select…</option>
            {CAREER_CATEGORIES.map((c) => (
              <option key={c.slug} value={c.slug}>{c.label}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="label-br" htmlFor="caf-status">Current Work Status</label>
          <input id="caf-status" className="input-br" value={form.current_status} onChange={update("current_status")} placeholder="Employed, between jobs, contracting, etc." data-testid="caf-status" />
        </div>
        <div className="sm:col-span-2">
          <label className="label-br" htmlFor="caf-experience">Relevant Experience</label>
          <textarea id="caf-experience" rows={3} className="textarea-br" value={form.relevant_experience} onChange={update("relevant_experience")} placeholder="Briefly describe relevant work, projects, or roles." data-testid="caf-experience" />
        </div>

        {/* Yes/No qualifiers */}
        <div className="sm:col-span-2 grid sm:grid-cols-2 gap-3 border-t border-gray-100 pt-4">
          {[
            ["has_construction_experience", "Construction experience?"],
            ["has_sales_experience", "Sales experience?"],
            ["has_reliable_transportation", "Reliable transportation?"],
            ["has_own_tools", "Own tools (if applicable)?"],
            ["comfortable_with_field_work", "Comfortable with field work?"],
            ["comfortable_with_commission", "Comfortable with commission / performance pay?"],
          ].map(([key, label]) => (
            <div key={key} className="flex items-center justify-between gap-3 bg-br-paper/40 border border-gray-100 rounded px-3 py-2">
              <div className="text-xs text-gray-700 leading-snug">{label}</div>
              <YesNoToggle name={key} value={form[key]} onChange={(v) => setForm((p) => ({ ...p, [key]: v }))} />
            </div>
          ))}
        </div>

        <div>
          <label className="label-br" htmlFor="caf-compensation">Desired Compensation</label>
          <input id="caf-compensation" className="input-br" value={form.desired_compensation} onChange={update("desired_compensation")} placeholder="Hourly / salary / commission split…" data-testid="caf-compensation" />
        </div>
        <div>
          <label className="label-br" htmlFor="caf-availability">Availability / Start Date</label>
          <input id="caf-availability" className="input-br" value={form.availability} onChange={update("availability")} placeholder="Immediate, 2 weeks notice…" data-testid="caf-availability" />
        </div>

        <div className="sm:col-span-2">
          <label className="label-br" htmlFor="caf-why">Why do you want to work with Black Ridge?</label>
          <textarea id="caf-why" rows={3} className="textarea-br" value={form.why_black_ridge} onChange={update("why_black_ridge")} data-testid="caf-why" />
        </div>
        <div className="sm:col-span-2">
          <label className="label-br" htmlFor="caf-notes">Anything else we should know?</label>
          <textarea id="caf-notes" rows={2} className="textarea-br" value={form.additional_notes} onChange={update("additional_notes")} data-testid="caf-notes" />
        </div>

        {/* File uploads */}
        <div className="sm:col-span-2">
          <div className="label-br mb-1">Resume + Portfolio / Project Photos</div>
          <div className="border-2 border-dashed border-gray-300 rounded-lg bg-br-paper/40 p-5">
            <div className="flex items-center gap-3">
              <Upload size={20} className="text-br-bronze" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-gray-900">Drop or browse files</div>
                <div className="text-[11px] text-gray-500">Up to {MAX_FILES} files, 3MB each. PDF or image preferred.</div>
              </div>
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="br-btn-dark text-[11px]"
                disabled={form.files.length >= MAX_FILES}
                data-testid="caf-file-pick"
              >
                Browse
              </button>
              <input ref={inputRef} type="file" multiple className="hidden" onChange={handleFiles} accept=".pdf,image/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" data-testid="caf-file-input" />
            </div>
            {form.files.length > 0 && (
              <ul className="mt-3 grid sm:grid-cols-2 gap-2" data-testid="caf-file-list">
                {form.files.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-gray-700 bg-white border border-gray-100 rounded px-2 py-1.5">
                    <Briefcase size={12} className="text-br-bronze shrink-0" />
                    <span className="truncate flex-1">{f.filename}</span>
                    <span className="text-[10px] text-gray-400">{formatBytes(f.size)}</span>
                    <button type="button" onClick={() => removeFile(i)} aria-label="Remove file" className="text-red-500 hover:text-red-700">
                      <Trash2 size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Honeypot */}
        <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={update("website")} className="hidden" aria-hidden="true" />

        {state.error && (
          <div className="sm:col-span-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3 flex items-start gap-2" data-testid="caf-error">
            <AlertCircle size={14} className="mt-0.5 shrink-0" /> {state.error}
          </div>
        )}

        <div className="sm:col-span-2 flex flex-wrap items-center gap-3 pt-1">
          <button type="submit" disabled={state.loading} className="br-btn-primary" data-testid="caf-submit">
            {state.loading ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : "Submit Application"}
          </button>
          <span className="text-[11px] text-gray-500">Form submissions land in the Black Ridge admin dashboard.</span>
        </div>
      </form>
    </div>
  );
});

export { CATEGORY_LABEL };
export default CareerApplicationForm;
